import { useState } from "react";
import { Edit3Icon, RotateCcw, TrashIcon } from "lucide-react";
import { useNavigate } from "react-router-dom";

import BreadCrumb from "../../../Components/BreadCrumb";
import TableListing from "../../../Components/TableListing";
import { Button } from "../../../Components/Buttons";
import { ToastMessage, getSelectedIds, handleExportExcel } from "../../../Components/Utils";
import { ROUTE_PATHS } from "../../../constants/routeConstants";
import { handleError } from "../../../stores/components/handleError";
import {
  useFetchProductsQuery,
  useFetchTrashedProductsQuery,
  useDeleteProductMutation,
  useDestroyProductMutation,
  useRestoreProductMutation,
  useBulkDeleteProductsMutation,
  useBulkDestroyProductsMutation,
  useBulkRestoreProductsMutation,
  useExportExcelTemplateQuery,
  useLazyExportExcelTemplateQuery,
  useExportExcelTemplateMutation,
  useExportProductExcelMutation,
} from "../../../stores/api/products.api";
import payloadHelper from "../../../stores/api/helpers/payload.helper";
import { product_image_url } from "../../../Components/ImageUrls";
import { product_excel_template } from "../../../Components/ApiURLs";

const EMPTY_PAGINATION = {
  data: [],
  current_page: 1,
  total_pages: 1,
  total_items: 0,
  current_items: 0,
  limit: 10,
};

const HEADERS = [
  { key: "image", label: "Image", sortable: false, type: "image", imageBaseUrl: product_image_url },
  { key: "name", label: "name" },
  { key: "brand.name", label: "Brand" },
  {
    key: "categories",
    label: "Category",
    format: (val) => val?.name ?? "-",
  },
  {
    key: "sub_categories",
    label: "Sub Category",
    format: (val) => val?.name ?? "-",
  },
  {
    key: "status",
    label: "Status",
    type: "status",
  },
];

const ProductList = () => {
  const navigate = useNavigate();
  const [trashed, setTrashed] = useState(false);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });

  const queryParams = payloadHelper.forList({
    page,
    limit,
    search,
    sortBy: sortConfig.key,
    sortOrder: sortConfig.direction,
  });

  const activeQuery = useFetchProductsQuery(queryParams, { skip: trashed });
  const trashedQuery = useFetchTrashedProductsQuery(queryParams, {
    skip: !trashed,
  });
  const { data: products = EMPTY_PAGINATION, isFetching } = trashed
    ? trashedQuery
    : activeQuery;

  const [deleteProduct] = useDeleteProductMutation();
  const [destroyProduct] = useDestroyProductMutation();
  const [restoreProduct] = useRestoreProductMutation();
  const [bulkDelete] = useBulkDeleteProductsMutation();
  const [bulkDestroy] = useBulkDestroyProductsMutation();
  const [bulkRestore] = useBulkRestoreProductsMutation();

  const resetPage = () => setPage(1);

  const handleSingle = (row, type = "delete") => {
    const verb =
      type === "restore"
        ? "Restore"
        : type === "destroy"
          ? "Destroy"
          : "Delete";
    ToastMessage({
      type: "warning",
      title: `${verb} Product`,
      message: `${verb} "${row.name}"?`,
      toastType: "confirm",
      onYes: async () => {
        try {
          if (type === "restore") await restoreProduct(row.id).unwrap();
          else if (type === "destroy") await destroyProduct(row.id).unwrap();
          else await deleteProduct(row.id).unwrap();
          ToastMessage({
            type: "success",
            title: "Done!",
            message: `Product ${verb.toLowerCase()}d.`,
          });
        } catch (err) {
          handleError(err, "Failed.");
        }
      },
    });
  };

  const handleBulk = (selectedItems, type = "delete") => {
    const ids = getSelectedIds(selectedItems);
    const verb =
      type === "restore"
        ? "Restore"
        : type === "destroy"
          ? "Destroy"
          : "Delete";
    ToastMessage({
      type: "warning",
      title: "Bulk Action",
      message: `${verb} ${ids.length} products?`,
      toastType: "confirm",
      onYes: async () => {
        try {
          const body = payloadHelper.forBulk(ids);
          if (type === "restore") await bulkRestore(body).unwrap();
          else if (type === "destroy") await bulkDestroy(body).unwrap();
          else await bulkDelete(body).unwrap();
          ToastMessage({
            type: "success",
            title: "Done!",
            message: `${ids.length} products ${verb.toLowerCase()}d.`,
          });
        } catch (err) {
          handleError(err, "Failed bulk action.");
        }
      },
    });
  };

  const handleTrashedChange = (value) => {
    setTrashed(value);
    resetPage();
  };
  const [templateExport, {isLoading: exportinTemplate}] = useExportExcelTemplateMutation();
  const [productExport,  {isLoading : exportingProduct}]  = useExportProductExcelMutation();
  const exportExcelTemplate = async () => {
    try {
      const response = await templateExport().unwrap();
       handleExportExcel({response:response, file_name:"product-template.xlsx"})
    } catch (error) {
       handleError(error)
    }
  };

  const exportProduct = async () => {
     try {
       const response = await productExport().unwrap();
       const date = new Date().toISOString().split("T")[0];
       const filename = `products-exported-${date}.xlsx`;
       handleExportExcel({response:response, file_name:filename})
     }
     catch(error) {
      handleError(error)
     }
  }
  return (
    <>
      <BreadCrumb title="Products" />

      <TableListing
        headers={HEADERS}
        paginationData={products}
        loading={isFetching}
        isExcelExport
        isExcelImport
        excelTemplateExport={exportExcelTemplate}
        exportData={exportProduct}
        createButton
        createLink={ROUTE_PATHS.CREATE_PRODUCT}
        handleBulk={handleBulk}
        statusModule="product"
        search={search}
        setSearch={setSearch}
        trashed={trashed}
        setTrashed={setTrashed}
        buttonLoading={exportingProduct || exportinTemplate}
        onTrashedChange={handleTrashedChange}
        onPageChange={setPage}
        onRowsPerPageChange={(value) => {
          setLimit(value);
          resetPage();
        }}
        onSearch={(value) => {
          setSearch(value);
          resetPage();
        }}
        onSortChange={(sort) => {
          setSortConfig(sort);
          resetPage();
        }}
        actions={(row) => (
          <div className="flex gap-2">
            {!trashed ? (
              <Button
                onClick={() =>
                  navigate(ROUTE_PATHS.CREATE_PRODUCT, {
                    state: { product: row },
                  })
                }
                title="Edit"
                icon={Edit3Icon}
                className="text-green-600 hover:bg-green-100 hover:text-green-700 dark:hover:bg-green-500/10"
              />
            ) : (
              <Button
                onClick={() => handleSingle(row, "restore")}
                title="Restore"
                icon={RotateCcw}
                className="text-green-600 hover:bg-green-100 hover:text-green-700 dark:hover:bg-green-500/10"
              />
            )}
            <Button
              onClick={() => handleSingle(row, trashed ? "destroy" : "delete")}
              title="Delete"
              icon={TrashIcon}
              className="text-red-600 hover:bg-red-100 hover:text-red-700 dark:hover:bg-red-500/10"
            />
          </div>
        )}
      />
    </>
  );
};

export default ProductList;
